<template>
  <Layout>
    <div class="container">
      <div class="pt-3 d-flex align-center justify-space-between">
        <v-btn @click="state.addModal = true" class="mt-3" data-cy="create-event-modal-btn"
          >Create Event</v-btn
        >
        <ButtonGroup
          :items="[
            {
              title: 'Current',
              value: 'current',
              dataCy: 'events-tab-current'
            },
            {
              title: 'Archived',
              value: 'archived',
              dataCy: 'events-tab-archived'
            }
          ]"
          v-model="state.tab"
        ></ButtonGroup>
      </div>
      <div class="events-container">
        <div class="events-list">
          <template v-for="conf in confStore[`${state.tab}_conferences`]" :key="conf.conference_id">
            <div class="event-card" :data-cy="`card-event-${conf.conference_id}`">
              <div>
                <Typography tag="div" align="center" color="gray800" size="1.25rem" weight="600">{{
                  conf.conference_name
                }}</Typography>
                <Typography tag="div" align="center" color="gray800" size="1rem" weight="600">
                  {{
                    dayjs(
                      minDate(
                        conf.conference_days.map((c) => c.start),
                        conf.open_tstamp
                      )
                    ).format("ddd, MMM D")
                  }}
                  -
                  {{
                    dayjs(
                      maxDate(
                        conf.conference_days.map((c) => c.end),
                        conf.closed_tstamp
                      )
                    ).format("ddd, MMM D")
                  }}</Typography
                >
              </div>
              <div class="actions">
                <RouterLink
                  style="text-decoration: none"
                  :to="{
                    name: 'school-edit-events',
                    params: {
                      eventId: conf.conference_id
                    }
                  }"
                  data-cy="goto-event-edit-btn"
                >
                  <div class="action">
                    <v-icon class="icon">mdi-calendar-edit</v-icon>
                    <span class="text">Edit</span>
                  </div>
                </RouterLink>
                <RouterLink
                  style="text-decoration: none"
                  :to="{
                    name: 'school-edit-schedule',
                    params: {
                      eventId: conf.conference_id
                    }
                  }"
                  data-cy="goto-event-schedule"
                >
                  <div class="action">
                    <v-icon class="icon">mdi-calendar-plus</v-icon>
                    <span class="text">Schedules</span>
                    <v-tooltip activator="parent" location="bottom">
                      Add/remove teachers and teams from the event and view/edit their schedule
                    </v-tooltip>
                  </div>
                </RouterLink>
                <RouterLink
                  style="text-decoration: none"
                  :to="{
                    name: 'school-reports',
                    params: {
                      eventId: conf.conference_id
                    }
                  }"
                  data-cy="goto-reports"
                >
                  <div class="action">
                    <v-icon class="icon">mdi-chart-box</v-icon>
                    <span class="text">Reports</span>
                  </div>
                </RouterLink>
              </div>
            </div>
          </template>

          <div
            v-if="confStore.loading && !confStore[`${state.tab}_conferences`].length"
            class="w-100"
            style="margin-top: 200px"
          >
            <LoadingState text="Loading Events..."></LoadingState>
          </div>
          <div
            v-if="!confStore.loading && !confStore[`${state.tab}_conferences`].length"
            class="w-100"
            style="margin-top: 200px"
          >
            <div class="empty-state" style="background: none">
              <figure>
                <IconListEmptyState />
              </figure>
              <div class="title">We couldn't find any events.</div>
              <p>Try reloading the page or adding a new event.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
    <AddEventModal v-model:show="state.addModal"></AddEventModal>
  </Layout>
</template>

<script setup lang="ts">
import Typography from "@/components/typography.vue";
import Layout from "./Layout.vue";

import { useConferenceStore } from "@/stores/teacher/conference";
import { reactive } from "vue";
import dayjs from "dayjs";
import LoadingState from "@/components/loadingState.vue";
import IconListEmptyState from "@/assets/icons/list-empty-state.svg";
import ButtonGroup from "@/components/ButtonGroup.vue";
import AddEventModal from "@/components/school/events/addEventModal.vue";

const state = reactive({
  tab: "current" as "current" | "archived",
  addModal: false
});

const confStore = useConferenceStore();

const minDate = (dates: string[], def: string) => {
  let min = dates[0];
  for (const date of dates) {
    const currDate = dayjs(date).toDate().getTime();
    if (currDate < dayjs(min).toDate().getTime()) min = date;
  }
  return min || def;
};
const maxDate = (dates: string[], def) => {
  let max = dates[0];
  for (const date of dates) {
    const currDate = dayjs(date).toDate().getTime();
    if (currDate > dayjs(max).toDate().getTime()) max = date;
  }

  return max || def;
};
</script>

<style scoped lang="scss">
@use "@/assets/_vars.scss" as *;
@use "@/assets/_breakpoints.scss" as breakpoints;

.events-container {
  padding: 30px 0;

  .events-list {
    display: flex;
    flex-wrap: wrap;
    gap: 20px;
    justify-content: center;

    .event-card {
      border: 3px solid $primary-75;
      border-radius: 16px;
      padding: 30px 50px;
      flex-basis: 100%;
      background-color: white;
      box-shadow: 3px 3px 10px 0 $gray-300;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      gap: 10px;

      @include breakpoints.media-breakpoint-up(sm) {
        & {
          flex-basis: calc(50% - 20px);
        }
      }
      @include breakpoints.media-breakpoint-up(lg) {
        & {
          flex-basis: calc(33% - 40px / 3);
        }
      }
      @include breakpoints.media-breakpoint-up(xxlg) {
        & {
          flex-basis: calc(25% - 60px / 4);
        }
      }

      .actions {
        display: flex;
        justify-content: center;
        width: 100%;
        gap: 20px;
        .action {
          color: $primary;
          display: flex;
          flex-direction: column;
          align-items: center;
          cursor: pointer;
          padding: 5px;
          border-radius: 4px;

          &:active {
            outline: 2px solid $primary-75;
          }
          gap: 5px;
          .icon {
            font-size: 1.5rem;
            font-weight: 600;
            flex-grow: 1;
          }
          .text {
            font-weight: 600;
            text-transform: uppercase;
          }
        }
      }
    }
  }
}
</style>
